'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

export default function CTASection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef?.current) {
      observer?.observe(sectionRef?.current);
    }

    return () => observer?.disconnect();
  }, []);

  return (
    <section className="py-16 bg-gradient-to-br from-primary via-accent to-secondary">
      <div className="container mx-auto px-4">
        <div ref={sectionRef} className={`max-w-4xl mx-auto text-center transform transition-all duration-700 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`
        }>
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full mb-6 hover:bg-white/30 transition-all duration-300">
            <Icon name="SparklesIcon" size={20} className="text-white" />
            <span className="text-sm font-medium text-white">Start Your Partnership Journey</span>
          </div>

          <h2 className="font-heading font-bold text-3xl lg:text-4xl xl:text-5xl text-white mb-6">
            Ready to Expand Your Pharmaceutical Business?
          </h2>

          <p className="text-base lg:text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Join 500+ global partners who trust Ashab Pharma for quality products and regulatory excellence. Let's discuss how we can support your pharmaceutical needs.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              href="/contact"
              className="inline-flex items-center px-8 py-4 bg-white text-primary font-cta font-semibold text-base rounded-md hover:-translate-y-2 hover:shadow-2xl hover:scale-105 transition-all duration-300 group">

              Schedule a Consultation
              <Icon name="CalendarIcon" size={20} className="ml-2 group-hover:rotate-12 transition-transform duration-300" />
            </Link>
            <Link
              href="/product-trading-portal"
              className="inline-flex items-center px-8 py-4 bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 font-semibold text-base rounded-md hover:bg-white/20 hover:border-white/50 hover:scale-105 transition-all duration-300 group">

              Browse Products
              <Icon name="CubeIcon" size={20} className="ml-2 group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </div>

          {/* Contact Options */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12 pt-12 border-t border-white/20">
            <div className="text-center group cursor-pointer">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full mb-3 group-hover:bg-white/30 group-hover:scale-110 transition-all duration-300">
                <Icon name="PhoneIcon" size={24} className="text-white" />
              </div>
              <h4 className="font-heading font-semibold text-base text-white mb-1">Call Us</h4>
              <p className="text-sm text-white/80 group-hover:text-white transition-colors duration-300">+91 9779962423</p>
            </div>
            <div className="text-center group cursor-pointer">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full mb-3 group-hover:bg-white/30 group-hover:scale-110 transition-all duration-300">
                <Icon name="EnvelopeIcon" size={24} className="text-white" />
              </div>
              <h4 className="font-heading font-semibold text-base text-white mb-1">Email Us</h4>
              <p className="text-sm text-white/80 group-hover:text-white transition-colors duration-300">info@ashabgroup.in</p>
            </div>
            <div className="text-center group cursor-pointer">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full mb-3 group-hover:bg-white/30 group-hover:scale-110 transition-all duration-300">
                <Icon name="MapPinIcon" size={24} className="text-white" />
              </div>
              <h4 className="font-heading font-semibold text-base text-white mb-1">Visit Us</h4>
              <p className="text-sm text-white/80 group-hover:text-white transition-colors duration-300">Amritsar, Punjab, India</p>
            </div>
          </div>
        </div>
      </div>
    </section>);

}